import { FilterConditionOutput } from '@shared/components/ui/AdvancedFilter';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useQueryParams } from '@shared/hooks/useQueryParams';
import { useTranslation } from 'react-i18next';
import { usePreInvoices } from './usePreInvoices';
import { getPreInvoicesFilterOptions } from '../config/filterOptions';

const fieldToApiMapping: Record<string, string> = {
  id: 'id',
  code: 'code',
  status: 'status',
  created_at: 'created_at',
  total_amount: 'total_amount',
};

const serializeConditions = (conditions: FilterConditionOutput[]) =>
  JSON.stringify(
    conditions.map(({ column, action, value }) => ({
      column,
      action,
      value,
    })),
  );

const parseFiltersFromParams = (value: unknown): FilterConditionOutput[] => {
  if (!Array.isArray(value)) {
    return [];
  }

  return value as FilterConditionOutput[];
};

/**
 * usePreInvoicesMain - Hook principal para gestionar toda la vista de PreInvoices
 *
 * Orquesta todos los aspectos de la gestión de prefacturas:
 * - Carga de datos paginados con infinite scroll
 * - Gestión de filtros avanzados y búsqueda (sincronizados con URL)
 * - Sorting de columnas (id, code, created_at, total_amount)
 * - Selección múltiple de prefacturas
 * - Sincronización bidireccional con query params de la URL
 *
 * @returns {Object} Estado completo de la vista con datos, handlers, filtros y configuración
 */
export const usePreInvoicesMain = () => {
  const { t } = useTranslation('preInvoicesModule');

  // 🎯 Función para cargar opciones dinámicamente (simplificado para PreInvoices)
  const loadColumnValues = useCallback(async () => {
    // Por ahora no hay filtros dinámicos, pero se puede agregar después
    return [];
  }, []);

  // 🎯 Obtener filterOptions
  const filterOptions = useMemo(() => getPreInvoicesFilterOptions(t, loadColumnValues), [t, loadColumnValues]);

  // 🎯 Query Params (sincronización con URL)
  // ⚠️ NO incluimos selectedColumns en URL - se persisten en localStorage
  const { params: queryParams, updateParams: setQueryParams } = useQueryParams({
    search: { type: 'string', default: '' },
    filters: { type: 'json', default: [] },
    sortBy: { type: 'string', default: 'id' },
    sortDirection: { type: 'string', default: 'desc' },
  });

  // 🎯 Estados locales
  const [searchTerm, setSearchTerm] = useState<string>(queryParams.search as string);
  const [filterConditions, setFilterConditions] = useState<FilterConditionOutput[]>(
    parseFiltersFromParams(queryParams.filters),
  );
  // 🎯 Las columnas seleccionadas NO se sincronizan con URL, solo con localStorage
  // ColumnSelector maneja la persistencia automáticamente
  const [selectedColumns, setSelectedColumns] = useState<string[]>([]);
  const [selectedPreInvoices, setSelectedPreInvoices] = useState<Set<number>>(new Set());

  // 🎯 Refs para detectar cambios en filtros
  const previousFiltersRef = useRef<string>('');
  const previousSearchRef = useRef<string>('');

  // 🎯 Sorting state
  const [sorting, setSorting] = useState<{
    field: string;
    direction: 'asc' | 'desc';
  } | null>(() => {
    const sortBy = queryParams.sortBy as string;
    const sortDirection = queryParams.sortDirection as 'asc' | 'desc';
    return sortBy ? { field: sortBy, direction: sortDirection || 'desc' } : null;
  });

  // 🎯 Construir searchFilter para la API
  const searchFilter = useMemo(() => {
    const filters: any[] = [];

    // Agregar condiciones de filtros avanzados
    if (filterConditions && filterConditions.length > 0) {
      filterConditions.forEach((condition) => {
        const apiField = fieldToApiMapping[condition.column] || condition.column;
        const operator = condition.action;
        if (!operator) {
          return;
        }

        // Operadores que no requieren valor
        if (operator === 'is_null' || operator === 'is_not_null') {
          filters.push({
            column: apiField,
            operator,
          });
          return;
        }

        const value = condition.value;

        // Normalizar valores para operadores que requieren arrays
        if (operator === 'in' || operator === 'not_in') {
          if (value === undefined || value === null || value === '') {
            return;
          }

          if (Array.isArray(value)) {
            filters.push({
              column: apiField,
              operator,
              value,
            });
          } else if (typeof value === 'string') {
            const normalized = value
              .split(',')
              .map((item) => item.trim())
              .filter(Boolean);

            if (normalized.length === 0) {
              return;
            }

            filters.push({
              column: apiField,
              operator,
              value: normalized,
            });
          } else {
            filters.push({
              column: apiField,
              operator,
              value: [value],
            });
          }

          return;
        }

        // Evitar enviar valores vacíos
        if (value === undefined || value === null || value === '') {
          return;
        }

        filters.push({
          column: apiField,
          operator,
          value,
        });
      });
    }

    return filters.length > 0
      ? {
          filters,
          logic: 'AND',
        }
      : undefined;
  }, [filterConditions]);

  const normalizedSearch = useMemo(() => {
    const value = searchTerm?.trim();
    return value ? value : undefined;
  }, [searchTerm]);

  // 🎯 Hook de datos con infinite scroll
  const { preInvoices, isLoading, isFetching, error, total, refetch, fetchNextPage, hasNextPage, isFetchingNextPage } =
    usePreInvoices({
      searchFilter,
      search: normalizedSearch,
      sortBy: sorting?.field || 'id',
      sortDirection: sorting?.direction || 'desc',
    });

  // 🎯 Detectar si hay filtros activos
  const hasActiveFilters = useMemo(() => {
    const currentFilters = serializeConditions(filterConditions);
    const currentSearch = searchTerm?.trim() || '';

    // Detectar cambios
    // const filtersChanged = currentFilters !== previousFiltersRef.current;
    // const searchChanged = currentSearch !== previousSearchRef.current;

    // Actualizar refs
    previousFiltersRef.current = currentFilters;
    previousSearchRef.current = currentSearch;

    // Hay filtros activos si hay condiciones o búsqueda
    return (filterConditions && filterConditions.length > 0) || (searchTerm && searchTerm.trim() !== '');
  }, [filterConditions, searchTerm]);

  // 🎯 Sincronizar estados locales con query params al montar
  useEffect(() => {
    if (queryParams.search) {
      setSearchTerm(queryParams.search as string);
    }
    if (queryParams.filters) {
      setFilterConditions(parseFiltersFromParams(queryParams.filters));
    }
  }, []); // Solo al montar

  // 🎯 Handlers de filtros
  const handleFilterChange = useCallback(
    (conditions: FilterConditionOutput[]) => {
      setFilterConditions(conditions);
      setQueryParams({ filters: conditions });
    },
    [setQueryParams],
  );

  const handleSearch = useCallback(
    (value: string) => {
      setSearchTerm(value);
      setQueryParams({ search: value });
    },
    [setQueryParams],
  );

  const handleColumnsChange = useCallback((columns: string[]) => {
    // 🎯 Solo actualizar estado local - NO sincronizar con URL
    // ColumnSelector se encarga de persistir en localStorage
    setSelectedColumns(columns);
  }, []);

  // 🎯 Handlers de sorting
  const handleSortingChange = useCallback(
    (newSorting: { field: string; direction: 'asc' | 'desc' } | null) => {
      setSorting(newSorting);
      if (newSorting) {
        setQueryParams({
          sortBy: newSorting.field,
          sortDirection: newSorting.direction,
        });
      }
    },
    [setQueryParams],
  );

  // 🎯 Handlers de selección
  const handleSelectionChanged = useCallback((selectedIds: Set<number>) => {
    setSelectedPreInvoices(selectedIds);
  }, []);

  const handleClearSelection = useCallback(() => {
    setSelectedPreInvoices(new Set());
  }, []);

  // 🎯 Props para componentes de vista
  const preInvoicesViewProps = useMemo(
    () => ({
      isLoading: isLoading && !isFetching,
      isFetching,
      sorting,
      onSortingChange: handleSortingChange,
    }),
    [isLoading, isFetching, sorting, handleSortingChange],
  );

  return {
    // Estados
    selectedPreInvoices,
    selectedColumns,

    // Datos
    preInvoices,
    isLoading,
    isFetching,
    error,
    total,

    // Props para componentes
    preInvoicesViewProps,

    // Handlers
    handleFilterChange,
    handleSearch,
    handleColumnsChange,
    handleClearSelection,
    refetch,

    // Props para infinite scroll
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,

    // Handlers de selección
    handleSelectionChanged,

    // Estado de filtros
    hasActiveFilters,
    filterConditions,
    searchTerm,
    filterOptions,
  };
};
